import React, { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";
import { GrFormNextLink, GrFormPreviousLink } from "react-icons/gr";
import { FaMapMarkerAlt } from "react-icons/fa"; // Ícone GPS
import { User, Trash2 } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import { useAuth } from "../../context/AuthContext";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import { useMobile } from "../Layout/UseResponsive";
import Toast from "../../helpers/Toast";
import { ValidDebtHourDTO } from "../../models/Assistance/ValidDebtHourDTO";
import { AssistanceExecutionDTO } from "../../models/AssistanceExecution/AssistanceExecutionDTO";
import { DeleteExecutionDTO } from "../../models/AssistanceExecution/DeleteExecutionDTO";
import { GetExecutionsByAssistanceDTO } from "../../models/AssistanceExecution/GetExecutionsByAssistanceDTO";
import { AssistanceService } from "../../services/AssistanceService";
import RenderEquipmentCountersComponent from "../Assistances/RenderEquipmentCountersComponent";
import ButtonComponent from "../ButtonComponent";
import InputComponent from "../InputComponent";
import ModalComponent from "../ModalComponent";
import ExecutionsDebtHoursTable from "./ExecutionsDebtHoursTable";
import ExecutionMapModal from "./ExecutionMapModal";
import "../../styles/AssistanceExecutions.css";


type AssistanceExecutionsComponentType = {
	assistanceId: number;
	reloadExecutions: boolean;
	mode: "workshop" | "backoffice";
	isComplete: boolean;

	onFetchExecutionsData: () => void;
};

export function ExecutionsTableComponent({ assistanceId, mode, reloadExecutions, isComplete, ...props }: AssistanceExecutionsComponentType) {
	const intl = useIntl();
	const isMobile = useMobile(700);
	const assistanceService = new AssistanceService();
	const auth = useAuth();

	const { availableModules } = useAvailableModulesContext();
	const isActiveCountersModule = availableModules.includes("IsActiveCountersModule");
	const isActiveErpSupportModule = availableModules.includes("IsActiveErpSupportModule");
	const isActiveWorkDoneAndPendingInExecutions = availableModules.includes("IsActiveWorkDoneAndPendingInExecutions");
	const technicianCanDebitHoursInExecutions = availableModules.includes("TechnicianCanDebitHoursInExecutions");

	const [isMapOpen, setIsMapOpen] = useState<boolean>(false);

	const [executions, setExecutions] = useState<AssistanceExecutionDTO[]>([]);
	const pageSizes: number[] = [5, 15, 30];
	const [pageSize, setPageSize] = useState<number>(pageSizes[0]);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [canPreviousPage, setCanPreviousPage] = useState<boolean>(false);
	const [canNextPage, setCanNextPage] = useState<boolean>(false);
	const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
	const [isLoadingEliminate, setIsLoadingEliminate] = useState<boolean>(false);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [idDeleted, setIdDeleted] = useState<number>(0);
	const [dateAndTecText, SetDateAndTecDate] = useState<string>("");
	const [totalDuration, setTotalDuration] = useState<number>(0);
	const [debtHours, setDebtHours] = useState<number>(0);
	const [isSync, setIsSync] = useState<boolean>(false);
	const [reload, setReload] = useState<boolean>(false);
	const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

	const toggleRow = (id: number) => {
		setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	const openLocation = (lat: number, lon: number) => {
		const url = `https://www.google.com/maps?q=${lat},${lon}`;
		window.open(url, '_blank');
	};

	const renderDateCellWithGPS = (date: string, lat: number | null, lon: number | null, type: 'start' | 'end') => {
		const hasLocation =
			lat !== null && lon !== null && lat !== 0 && lon !== 0;

		return (
			<div
				style={hasLocation ? { display: 'flex', alignItems: 'center', gap: '8px' } : undefined}
			>
				<label>{date}</label>
				{hasLocation && (
					<button
						onClick={() => openLocation(lat, lon)}
						style={{
							background: 'none',
							border: 'none',
							color: '#007bff',
							cursor: 'pointer',
							padding: '2px',
							fontSize: '12px',
							display: 'flex',
							alignItems: 'center'
						}}
						title={`Ver localização ${type === 'start' ? 'de início' : 'de fim'} no mapa`}
					>
						<FaMapMarkerAlt />
					</button>
				)}
			</div>
		);
	};

	const hasExecutionsWithLocation = executions.some(exec =>
		(exec.startLocationLat !== null && exec.startLocationLon !== null) ||
		(exec.endLocationLat !== null && exec.endLocationLon !== null)
	);

	const loadExecutions = async () => {
		setIsLoadingTable(true);

		const params: GetExecutionsByAssistanceDTO = {
			assistanceId: assistanceId,
			currentPage: currentPage,
			pageSize: pageSize,
		};

		var response = await assistanceService.GetExecutions(params);

		setIsLoadingTable(false);

		if (response.success === true) {
			setExecutions(response.items);
			setTotalDuration(response.totalDurationExecution);
			setTotalPages(response.totalPages);
			setCanNextPage(response.totalPages > currentPage);
			setCanPreviousPage(currentPage > 1);

			//Se a pesquisa tiver retornado múltiplas páginas e a página atual
			//for superior ao nº de páginas que chegaram mandamos o user para a página 1
			if (response.totalPages > 0 && currentPage > response.totalPages) {
				setCurrentPage(1);
			}
		}
	};

	const previousPage = () => {
		if (currentPage > 1) {
			setCurrentPage(currentPage - 1);
		}
	};

	const nextPage = () => {
		if (currentPage + 1 <= totalPages) {
			setCurrentPage(currentPage + 1);
		}
	};

	const deleteExecution = async (executionId: number) => {
		const params: DeleteExecutionDTO = {
			executionId: executionId,
		};

		setIsLoadingEliminate(true);
		var response = await assistanceService.DeleteExecution(params);
		setIsLoadingEliminate(false);

		if (response.success !== true || response.obj == null) {
			Toast.Show("error", response.message);
			return;
		}
		Toast.Show("success", "Execução removida com sucesso");
		loadExecutions();
	};

	const InsertDebtHours = async () => {
		var assistance = await assistanceService.GetById(assistanceId);
		if (assistance.success === false || assistance.obj === null) {
			Toast.Show("error", assistance.message);
			return;
		}
		var sendData: ValidDebtHourDTO = {
			numDoc: assistance.obj.erpNumDoc !== null ? assistance.obj.erpNumDoc : 0,
			serie: assistance.obj.erpSerie !== null ? assistance.obj.erpSerie : "",
			typeDoc: assistance.obj.erpDocType !== null ? assistance.obj.erpDocType : "",
			debtHour: debtHours,
		};
		setIsButtonLoading(true);
		var response = await assistanceService.InsertDebtHours(sendData);
		setIsButtonLoading(false);

		if (response.success === false || response.obj === null) {
			Toast.Show("error", response.message);
			return;
		}
		Toast.Show("success", response.message);
		setReload(!reload);
	};

	useEffect(() => {
		if (assistanceId !== 0) {
			loadExecutions();
		}
	}, [assistanceId, currentPage, pageSize, reloadExecutions]);

	const renderMobileExecutionCard = (execution: AssistanceExecutionDTO) => {
		const canRemove = (mode === 'backoffice') || (mode === 'workshop' && execution.technicianName === auth?.currentUser?.username);

		return (
			<div key={execution.id} className="execution-card-mobile">
				<div className="execution-card-header-mobile">
					<div className="execution-card-technician-mobile">
						<User size={18} />
						<span>{execution.technicianName}</span>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<div className="execution-card-duration-badge">
							{execution.durationHours}h Duração
						</div>
						{canRemove && !isLoadingTable && (
							<button
								onClick={() => {
									setIdDeleted(execution.id);
									SetDateAndTecDate(
										execution.technicianName +
										" com a data " +
										execution.executionStart +
										" - " +
										execution.executionEnd
									);
									setIsOpen(true);
								}}
								disabled={isLoadingEliminate}
								style={{
									background: 'none',
									border: 'none',
									color: '#dc3545',
									cursor: isLoadingEliminate ? 'not-allowed' : 'pointer',
									padding: '4px',
									display: 'flex',
									alignItems: 'center',
									opacity: isLoadingEliminate ? 0.5 : 1
								}}
								title="Apagar execução"
							>
								<Trash2 size={18} />
							</button>
						)}
					</div>
				</div>

				<div className="execution-card-dates-mobile">
					<div className="execution-date-item-mobile">
						<span className="execution-date-label-mobile">Início</span>
						<div className="execution-date-value-mobile">{execution.executionStart}</div>
					</div>
					<div className="execution-date-item-mobile">
						<span className="execution-date-label-mobile">Fim</span>
						<div className="execution-date-value-mobile">{execution.executionEnd}</div>
					</div>
				</div>

				{isActiveWorkDoneAndPendingInExecutions && (
					<>
						<div className="execution-card-work-mobile">
							<span className="execution-work-label-mobile">Trabalho Realizado:</span>
							<div className="execution-work-content-mobile">
								{execution.workDone?.trim() || "Não preenchido"}
							</div>
						</div>
						{execution.pending?.trim() && (
							<div className="execution-card-work-mobile">
								<span className="execution-work-label-mobile">Trabalho Pendente:</span>
								<div className="execution-work-content-mobile">
									{execution.pending.trim()}
								</div>
							</div>
						)}
					</>
				)}
			</div>
		);
	};

	return (
		<>
			<ModalComponent
				IsOpen={isOpen}
				text={`Tem a certeza que quer remover a execução do técnico ${dateAndTecText}`}
				OnClickRight={() => setIsOpen(false)}
				OnClickLeft={() => {
					setIsOpen(false);
					deleteExecution(idDeleted);
				}}
				title={"Remover Execução"}
				buttonRight={"Não"}
				buttonLeft={"Sim"}
			/>

			<Row style={{ margin: isMobile ? "0" : "1rem 0px" }}>
				<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginTop: "1rem", marginBottom: "1rem" }}>
					<h5 className={isMobile ? "executions-list-title-mobile" : ""} style={{ margin: 0 }}>
						<FormattedMessage id={"Screens.Assistances.Executions.Table.Title"}></FormattedMessage>
					</h5>
					{hasExecutionsWithLocation && !isMobile && (
						<ButtonComponent
							onClick={() => setIsMapOpen(true)}
							text={intl.formatMessage({ id: "Screens.AssistanceExecutions.Button.SeeMap" })}
							style={{ minWidth: "100px" }}
						/>
					)}
				</div>

				{/* Desktop Table View */}
				{!isMobile && (
					<Row style={{ margin: "1rem 0px", overflowX: "auto", paddingBottom: "1px", paddingTop: "1px" }}>
						<table>
							<thead>
								<tr>
									<th>
										<FormattedMessage id={"Screens.Assistances.Executions.Table.Technician"}></FormattedMessage>
									</th>
									<th>
										<FormattedMessage id={"Screens.Assistances.Executions.Table.StartDate"}></FormattedMessage>
									</th>
									<th>
										<FormattedMessage id={"Screens.Assistances.Executions.Table.EndDate"}></FormattedMessage>
									</th>
									<th>
										<FormattedMessage id={"Screens.Assistances.Executions.Table.Duration"}></FormattedMessage>
									</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{executions.map((m) => {
									const canRemove = (mode === 'backoffice') || (mode === 'workshop' && m.technicianName === auth?.currentUser?.username);
									const isExpanded = expandedRows[m.id];

									return (
										<React.Fragment key={m.id}>
											<tr>
												<td width={250}>
													<label>{m.technicianName}</label>
												</td>

												<td width={250}>
													{renderDateCellWithGPS(m.executionStart, m.startLocationLat ?? 0, m.startLocationLon ?? 0, 'start')}
												</td>

												<td width={250}>
													{renderDateCellWithGPS(m.executionEnd, m.endLocationLat?.valueOf() ?? 0, m.endLocationLon ?? 0, 'end')}
												</td>
												<td width={250}>
													<label>{m.durationHours}</label>
												</td>
												<td width={100}>
													<div style={{ display: "inline-flex", height: "30px" }}>
														{isActiveWorkDoneAndPendingInExecutions &&
															<ButtonComponent
																text={isExpanded ? intl.formatMessage({ id: "ExecutionsTableComponent.Close" }) : intl.formatMessage({ id: "ExecutionsTableComponent.Open" })}
																onClick={() => toggleRow(m.id)}
															/>
														}
														<ButtonComponent
															text={intl.formatMessage({ id: "ExecutionsTableComponent.Remove" })}
															icon={<FiX />}
															disabled={!canRemove || (isComplete === false ? isLoadingTable : true)}
															loading={isLoadingEliminate}
															onClick={() => {
																setIdDeleted(m.id);
																SetDateAndTecDate(
																	m.technicianName +
																	" com a data " +
																	m.executionStart +
																	" - " +
																	m.executionEnd
																);
																setIsOpen(true);
															}}
														/>
													</div>
												</td>
											</tr>
											{isExpanded && (
												<tr>
													<td colSpan={5}>
														<div style={{ display: "flex", flexDirection: "column" }}>
															<div style={{ display: "flex" }}>
																<div style={{ width: "50%", padding: "0.5rem", textAlign: "left" }}>
																	<p style={{ marginBottom: "8px" }}><strong>Trabalho Realizado:</strong></p>
																	{m.workDone?.trim() ? m.workDone : "Não preenchido"}
																</div>
																<div style={{ width: "50%", padding: "0.5rem", textAlign: "left" }}>
																	<p style={{ marginBottom: "8px" }}><strong>Trabalho Pendente:</strong></p>
																	{m.pending?.trim() ? m.pending : "Não preenchido"}
																</div>
															</div>

															{/* Display counters when both modules are active and counters data exists */}
															{isActiveCountersModule && isActiveErpSupportModule && m.counters && m.counters.length > 0 && (
																<div style={{ textAlign: "left", borderTop: "1px solid #eee", marginTop: "15px", padding: ".5rem" }}>
																	<label style={{ margin: "0px", padding: "5px 0px", fontWeight: "bold", display: "block" }}>
																		{intl.formatMessage({ id: "Screens.AssistanceEditB.CountersTitle" })}
																	</label>
																	<RenderEquipmentCountersComponent
																		isComplete={true}
																		counterValues={m.counters}
																		loadingCounters={false}
																		errorCounters={null}
																		hideLastExecutionReading={true}
																	/>
																</div>
															)}
														</div>
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
							</tbody>
						</table>
					</Row>
				)}

				{/* Mobile Card View */}
				{isMobile && (
					<div className="executions-mobile-only">
						{executions.map((execution) => renderMobileExecutionCard(execution))}
					</div>
				)}

				<Row style={{ margin: isMobile ? "0" : "1rem 0px" }}>
					{isMobile ? (
						<div style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							padding: '1rem 0.5rem',
							opacity: isLoadingTable ? 0.5 : 1,
							width: '100%'
						}}>
							<button
								onClick={() => previousPage()}
								disabled={!canPreviousPage || isLoadingTable}
								style={{
									background: 'none',
									border: 'none',
									color: canPreviousPage && !isLoadingTable ? '#007bff' : '#ccc',
									cursor: canPreviousPage && !isLoadingTable ? 'pointer' : 'not-allowed',
									fontSize: '18px',
									padding: '4px 8px'
								}}
							>
								&lt;
							</button>

							<span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
								Página {currentPage} de {totalPages}
							</span>

							<button
								onClick={() => nextPage()}
								disabled={!canNextPage || isLoadingTable}
								style={{
									background: 'none',
									border: 'none',
									color: canNextPage && !isLoadingTable ? '#007bff' : '#ccc',
									cursor: canNextPage && !isLoadingTable ? 'pointer' : 'not-allowed',
									fontSize: '18px',
									padding: '4px 8px'
								}}
							>
								&gt;
							</button>

							<div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', paddingLeft: '12px' }}>
								<span style={{ fontSize: '13px', color: '#666', whiteSpace: 'nowrap' }}>Por página:</span>
								<select
									disabled={isLoadingTable}
									value={pageSize}
									onChange={(e) => setPageSize(Number(e.target.value))}
									style={{
										padding: '4px 8px',
										border: '1px solid #ddd',
										borderRadius: '4px',
										fontSize: '14px',
										cursor: isLoadingTable ? 'not-allowed' : 'pointer',
										backgroundColor: 'white'
									}}
								>
									{pageSizes.map((size) => (
										<option key={size} value={size}>
											{size}
										</option>
									))}
								</select>
							</div>
						</div>
					) : (
						<div className="pagination" style={{ opacity: isLoadingTable === true ? 0.5 : 1 }}>
							<button className="paginationButton" onClick={() => previousPage()} disabled={!canPreviousPage || isLoadingTable}>
								<GrFormPreviousLink size={20} />
							</button>

							<span className="paginationNumberIndicator">
								<FormattedMessage id="Common.Labels.Page" /> <strong>{currentPage}</strong>{" "}
								<FormattedMessage id="Common.Labels.Of" /> <strong>{totalPages}</strong>
							</span>

							<button className="paginationButton" onClick={() => nextPage()} disabled={!canNextPage || isLoadingTable}>
								<GrFormNextLink size={20} />
							</button>

							<div className="paginationRecordSize">
								<span>
									<FormattedMessage id="Common.Labels.RecordsPerPage" />
								</span>
								<select
									className="paginationRecordSizeSelect"
									disabled={isLoadingTable}
									value={pageSize}
									onChange={(e) => {
										setPageSize(Number(e.target.value));
									}}
								>
									{pageSizes.map((pageSize) => {
										return (
											<option key={pageSize} value={pageSize}>
												{pageSize}
											</option>
										);
									})}
								</select>
							</div>
						</div>
					)}
				</Row>
			</Row>

			{isMobile ? (
				<div className="total-hours-mobile">
					<div className="total-hours-label-mobile">
						{intl.formatMessage({ id: "Screens.Assistances.Executions.Label.RegistedHours" })}
					</div>
					<div className="total-hours-value-mobile">{totalDuration}h</div>
				</div>
			) : (
				<Row style={{ margin: "1rem 0px", marginTop: "4.5%" }}>
					<InputComponent
						xl={4}
						lg={4}
						md={4}
						sm={12}
						xs={12}
						style={{ width: "100%" }}
						type={"number"}
						value={totalDuration}
						label={intl.formatMessage({ id: "Screens.Assistances.Executions.Label.RegistedHours" })}
						readOnly={true}
					/>
					{mode === "backoffice" || (mode === "workshop" && technicianCanDebitHoursInExecutions) ? (
						<>
							<InputComponent
								xl={4}
								lg={4}
								md={12}
								sm={12}
								xs={12}
								style={{ width: "100%" }}
								type={"number"}
								value={debtHours}
								label={intl.formatMessage({ id: "Screens.Assistances.Executions.Label.HoursToDebit" })}
								readOnly={isComplete}
								onChange={(t: number) => {
									setDebtHours(t);
									setIsSync(false);
								}}
							/>
							<Col xl={4} lg={4} md={4} sm={4} xs={12}>
								<ButtonComponent
									loading={isButtonLoading}
									style={{ width: "100%", marginTop: "30px" }}
									text={intl.formatMessage({ id: "ExecutionsTableComponent.ValidateDebtHours" })}
									onClick={() => InsertDebtHours()}
									disabled={isComplete}
								/>
								{isSync === true ? <span style={{ fontSize: "9px" }}>(Informação já sincronizada)</span> : ""}
							</Col>
						</>
					) : (
						""
					)}
				</Row>
			)}

			{mode === "backoffice" || (mode === "workshop" && technicianCanDebitHoursInExecutions) ? (
				<Row style={{ margin: "1rem 0px" }}>
					<ExecutionsDebtHoursTable assistanceId={assistanceId} reload={reload} />
				</Row>
			) : (
				""
			)}

			<ExecutionMapModal
				isOpen={isMapOpen}
				onClose={() => setIsMapOpen(false)}
				executions={executions}
			/>

		</>
	);
}